import React, { createContext, useEffect, useMemo } from 'react'
import useEngageDefaultSettings from '../../../../hooks/useEngageDefaultSettings'
import { DEFAULT_SETTINGS_ACTIONS } from '../constants'

export const DefaultSettingsContext = createContext({})

/**
 * Provider for the default settings data and api functions for the engage modals
 */
function DefaultSettingsProvider(props) {
  const { children } = props // eslint-disable-line react/prop-types

  const {
    // get
    fetchEngageDefaultSettings,
    isFetchingEngageDefaultSettings,
    errorFetchingEngageDefaultSettings,
    engageDefaultSettings,
    // post
    createEngageDefaultSettings,
    isCreatingEngageDefaultSettings,
    errorCreatingEngageDefaultSettings,
    successCreatingEngageDefaultSettings,
    // put
    updateEngageDefaultSettings,
    isUpdatingEngageDefaultSettings,
    errorUpdatingEngageDefaultSettings,
    successUpdatingEngageDefaultSettings,

    // delete
    deleteEngageDefaultSettings,
    isDeletingEngageDefaultSettings,
    errorDeletingEngageDefaultSettings,

    // reset
    resetEngageDefaultSettingsControls,
  } = useEngageDefaultSettings()

  // fetch all settings for an org and its customers on mount
  useEffect(() => {
    fetchEngageDefaultSettings()
  }, [fetchEngageDefaultSettings])

  // transforms the data to be consumed by the modals
  const orgSettings = useMemo(
    () => engageDefaultSettings.filter((setting) => setting.customerId === null),
    [engageDefaultSettings],
  )

  const orgSettingsByAction = useMemo(
    () =>
      orgSettings.reduce((acc, setting) => {
        acc[setting.action] = setting
        return acc
      }, {}),
    [orgSettings],
  )

  // this is for always showing all settings in org table, even if they were not created yet
  const preFilledOrgSettings = useMemo(
    () => Object.values(DEFAULT_SETTINGS_ACTIONS).map((action) => orgSettingsByAction[action] || { action }),
    [orgSettingsByAction],
  )

  const customerSettings = useMemo(
    () => engageDefaultSettings.filter((setting) => setting.customerId !== null),
    [engageDefaultSettings],
  )

  const provided = useMemo(
    () => ({
      // get
      fetchEngageDefaultSettings,
      isFetchingEngageDefaultSettings,
      errorFetchingEngageDefaultSettings,
      // post
      createEngageDefaultSettings,
      isCreatingEngageDefaultSettings,
      errorCreatingEngageDefaultSettings,
      successCreatingEngageDefaultSettings,
      // put
      updateEngageDefaultSettings,
      isUpdatingEngageDefaultSettings,
      errorUpdatingEngageDefaultSettings,
      successUpdatingEngageDefaultSettings,
      // delete
      deleteEngageDefaultSettings,
      isDeletingEngageDefaultSettings,
      errorDeletingEngageDefaultSettings,
      // data
      engageDefaultSettings,
      orgSettings,
      orgSettingsByAction,
      preFilledOrgSettings,
      customerSettings,
      // reset
      resetEngageDefaultSettingsControls,
    }),
    [
      fetchEngageDefaultSettings,
      isFetchingEngageDefaultSettings,
      errorFetchingEngageDefaultSettings,
      engageDefaultSettings,
      createEngageDefaultSettings,
      isCreatingEngageDefaultSettings,
      errorCreatingEngageDefaultSettings,
      successCreatingEngageDefaultSettings,
      orgSettings,
      orgSettingsByAction,
      preFilledOrgSettings,
      customerSettings,
      updateEngageDefaultSettings,
      isUpdatingEngageDefaultSettings,
      errorUpdatingEngageDefaultSettings,
      successUpdatingEngageDefaultSettings,
      deleteEngageDefaultSettings,
      isDeletingEngageDefaultSettings,
      errorDeletingEngageDefaultSettings,
      resetEngageDefaultSettingsControls,
    ],
  )

  return <DefaultSettingsContext.Provider value={provided}>{children}</DefaultSettingsContext.Provider>
}

export default DefaultSettingsProvider
